import * as React from 'react';
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';

interface ExtendedOpenGraphProps extends Partial<OpenGraphMetadata> {
	priceCurrency?: string;
	priceAmount?: string;
}

export interface OpenGraphMetadata {
	title: string;
	description: string;
	image?: string;
	type: string;
	url?: string;
}

export interface Hreflang {
	url: string;
	language: string;
}

export interface PageMetadata {
	title: string;
	description: string;
	keywords: string;
	noIndex: boolean;
	noFollow: boolean;
	canonicalLink?: string;
	openGraph?: OpenGraphMetadata;
	hreflangs?: Hreflang[];
}

interface HeadProps {
	metaData: PageMetadata;
	openGraph?: ExtendedOpenGraphProps;
	children?: React.ReactNode;
}

const Head: React.FunctionComponent<HeadProps> = ({ children, metaData, openGraph }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { title, description, keywords, canonicalLink, noIndex, noFollow, hreflangs } = metaData;

	const openGraphData: ExtendedOpenGraphProps = {
		title: openGraph?.title || metaData.openGraph?.title || title,
		description: openGraph?.description || metaData.openGraph?.description || description,
		type: openGraph?.type || 'website',
		image: openGraph?.image ?? metaData.openGraph?.image,
		url: openGraph?.url || metaData.openGraph?.url || canonicalLink,
		priceCurrency: openGraph?.priceCurrency,
		priceAmount: openGraph?.priceAmount,
	};

	const robots: string[] = [];
	if (noIndex) {
		robots.push('noindex');
	}
	if (noFollow) {
		robots.push('nofollow');
	}

	return (
		<Helmet>
			<title>{title}</title>
			{description && <meta name="description" content={description} />}
			{keywords && <meta name="keywords" content={keywords} />}
			<link rel="icon" type="image/png" sizes="32x32" href="/favicons/favicon-32x32.png" />
			{canonicalLink && <link rel="canonical" href={canonicalLink} />}
			{!!robots.length && <meta name="robots" content={robots.join(',')} />}
			{!noIndex && <meta name="robots" content="max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
			{hreflangs &&
				hreflangs.map((item) => (
					<link key={item.language} rel="alternate" href={item.url} hrefLang={item.language} />
				))}
			<meta property="og:site_name" content={translations?.data.shared.siteName} />
			{openGraphData.title && <meta property="og:title" content={openGraphData.title} />}
			{openGraphData.description && <meta property="og:description" content={openGraphData.description} />}
			{openGraphData.image && <meta property="og:image" content={openGraphData.image} />}
			{openGraphData.type && <meta property="og:type" content={openGraphData.type} />}
			{openGraphData.url && <meta property="og:url" content={openGraphData.url} />}
			{openGraphData.priceCurrency && <meta property="og:price:currency" content={openGraphData.priceCurrency} />}
			{openGraphData.priceAmount && <meta property="og:price:amount" content={openGraphData.priceAmount} />}
			{children}
		</Helmet>
	);
};

export default Head;
